import { FC, ReactNode, useState } from 'react';
import { useQueryClient } from 'react-query';
import classNames from 'classnames';
import { IconButton } from '@lambdacurry/component-library';
import { useUpsertExtraLineItemMutation } from 'lib/home/generated/queries';
import { MedusaCart, MedusaProductVariant, OrdersQuery } from 'lib/home/generated/types/api';
import { medusaPriceAmountToMoney } from 'lib/home/util/currency';
import { AlertTextButton, Card, CardHeader, MealImage, MoreInfoTextButton } from 'lib/components';
import { NutrientLabelModal } from '..';

interface ExtraCardProps {
  extra: MedusaProductVariant;
  quantity?: number;
  price: number;
  alert?: boolean;
  actions?: ReactNode;
  className?: string;
  index: number;
}

export const ExtraCard: FC<ExtraCardProps> = ({ alert, extra, quantity, price, className, index }) => {
  const [nutrientLabelOpen, setNutrientLabelOpen] = useState(false);
  const queryClient = useQueryClient();

  const updateCurrentCart = (cart: MedusaCart) => {
    queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
      query.me.subscription.current_order.medusa_cart = cart;
      return query;
    });
  };

  const { mutate: mutateUpsertExtraLineItem, isLoading } = useUpsertExtraLineItemMutation({
    onSuccess: data => {
      const {
        upsertExtraLineItem: { medusa_cart }
      } = data;

      updateCurrentCart(medusa_cart);
    }
  });

  const product = extra?.product;
  if (!product) return null;
  const { title, thumbnail } = product;

  return (
    <Card className={classNames('extra-card !flex-row ', className)}>
      <CardHeader className="relative min-w-[6rem] w-44 p-3">
        <MealImage className="w-full" url={thumbnail} title={title} />
      </CardHeader>

      <div className="flex flex-col w-full justify-around px-3 pt-3 pb-2">
        <div className="flex justify-between">
          <p className="extra-card__title my-0 font-bold">{title}</p>
          <p className="my-0 ml-4 mr-2">{medusaPriceAmountToMoney((quantity ?? 1) * price)}</p>
        </div>
        <div className="flex items-center justify-between align-baseline flex-wrap">
          {alert ? (
            <AlertTextButton className="text-alert" onClick={() => setNutrientLabelOpen(true)}>
              Contains Allergens
            </AlertTextButton>
          ) : (
            <MoreInfoTextButton onClick={() => setNutrientLabelOpen(true)} />
          )}

          <div className="flex items-center ml-auto">
            {quantity && (
              <>
                <IconButton
                  data-cy={`subtract-extra-btn-${quantity ? 'selected' : 'option'}-${index}`}
                  disabled={isLoading}
                  className="!text-black"
                  name="subtract"
                  aria-label="add item to cart"
                  icon="subtract"
                  onClick={() =>
                    mutateUpsertExtraLineItem({
                      input: { product_variant_id: extra.id, quantity: quantity ? quantity - 1 : 0 }
                    })
                  }
                />
                <div className="mx-2">{quantity ?? 0}</div>
              </>
            )}
            <IconButton
              data-cy={`add-extra-btn-${quantity ? 'selected' : 'option'}-${index}`}
              disabled={isLoading}
              className="!text-black"
              name="add"
              aria-label="remove item from cart"
              icon="add"
              onClick={() =>
                mutateUpsertExtraLineItem({
                  input: { product_variant_id: extra.id, quantity: quantity ? quantity + 1 : 1 }
                })
              }
            />
          </div>
        </div>
      </div>

      <NutrientLabelModal
        closeButton={false}
        isOpen={nutrientLabelOpen}
        meal={extra}
        onAfterClose={() => setNutrientLabelOpen(false)}
        setIsOpen={setNutrientLabelOpen}
      />
    </Card>
  );
};
